import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Zap } from "lucide-react";
import { WhatsGlyph } from "./WhatsGlyph";
import { IMAGES, waLink, DEFAULT_MSG, TRIAL_MSG, scrollToId } from "@/data/site";

const line = (i) => ({
  initial: { y: "115%" },
  animate: { y: "0%" },
  transition: { duration: 1, delay: 0.15 + i * 0.14, ease: [0.16, 1, 0.3, 1] },
});

const stats = [
  { value: "4K", label: "Ultra HD" },
  { value: "ZERO", label: "Travamento" },
  { value: "24/7", label: "Suporte WhatsApp" },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 70]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, -50]);

  return (
    <section
      id="inicio"
      ref={ref}
      className="relative overflow-hidden pt-40 pb-20 sm:pt-44 lg:pt-44 lg:pb-28"
      data-testid="hero-section"
    >
      {/* background layers */}
      <div
        className="bg-grid absolute inset-0"
        style={{
          maskImage:
            "radial-gradient(ellipse 85% 70% at 50% 25%, black 25%, transparent 78%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 85% 70% at 50% 25%, black 25%, transparent 78%)",
        }}
        aria-hidden="true"
      />
      <motion.div
        style={{ y: glowY }}
        className="pointer-events-none absolute inset-0"
        aria-hidden="true"
      >
        <div className="absolute -top-32 left-1/4 h-[480px] w-[480px] rounded-full bg-neon/15 blur-[140px]" />
        <div className="absolute right-[8%] top-1/3 h-[420px] w-[420px] rounded-full bg-vio/25 blur-[140px]" />
        <div className="absolute bottom-0 left-[45%] h-[300px] w-[300px] rounded-full bg-wa/10 blur-[120px]" />
      </motion.div>

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 sm:px-8 lg:grid-cols-12 lg:gap-10">
        {/* type column */}
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
          >
            <span className="inline-flex items-center gap-2.5 rounded-full border border-neon/30 bg-neon/5 px-4 py-2 font-mono text-[10px] font-medium uppercase tracking-[0.25em] text-neon sm:text-[11px]">
              <span className="animate-blink h-1.5 w-1.5 rounded-full bg-wa" />
              IPTV Premium • Ativação imediata
            </span>
          </motion.div>

          <h1 className="mt-7 font-display text-[clamp(3rem,7.5vw,6.25rem)] font-extrabold uppercase leading-[0.94] tracking-tight text-white">
            <span className="mask-line">
              <motion.span className="block" {...line(0)}>
                Toda a TV que
              </motion.span>
            </span>
            <span className="mask-line">
              <motion.span className="block" {...line(1)}>
                você ama em <span className="text-neon">4K,</span>
              </motion.span>
            </span>
            <span className="mask-line">
              <motion.span className="text-outline-neon block" {...line(2)}>
                sem travar.
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="mt-7 max-w-xl text-base leading-relaxed text-mist sm:text-lg"
          >
            Filmes, séries, futebol ao vivo e milhares de canais em qualidade
            extrema — no seu aparelho, no seu horário. Ativação imediata pelo
            WhatsApp.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.72, ease: [0.16, 1, 0.3, 1] }}
            className="mt-9 flex flex-col gap-4 sm:flex-row sm:items-center"
          >
            <a
              href={waLink(DEFAULT_MSG)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-cta"
              className="group inline-flex items-center justify-center gap-3 rounded-full bg-wa px-8 py-4 font-display text-lg font-bold uppercase tracking-wider text-[#052e16] shadow-[0_12px_40px_-10px_rgba(37,211,102,0.55)] transition-colors hover:bg-wa-hover"
            >
              <WhatsGlyph className="h-5 w-5 transition-transform group-hover:scale-110" />
              Falar no WhatsApp
            </a>
            <a
              href={waLink(TRIAL_MSG)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-trial-cta"
              className="inline-flex items-center justify-center gap-2.5 rounded-full border border-neon/40 bg-neon/5 px-8 py-4 font-display text-lg font-bold uppercase tracking-wider text-neon transition-colors hover:bg-neon hover:text-ink"
            >
              <Zap className="h-5 w-5" />
              Teste grátis 12h
            </a>
          </motion.div>

          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            onClick={() => scrollToId("#planos")}
            data-testid="hero-plans-cta"
            className="mt-5 font-mono text-xs uppercase tracking-[0.2em] text-mist underline-offset-4 transition-colors hover:text-neon hover:underline"
          >
            ou ver planos e preços ↓
          </motion.button>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.9 }}
            className="mt-12 grid max-w-lg grid-cols-3 gap-6"
            data-testid="hero-stats"
          >
            {stats.map((s) => (
              <div key={s.label} className="border-l border-white/10 pl-4">
                <p className="font-display text-2xl font-extrabold text-white sm:text-3xl">
                  {s.value}
                </p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-mist">
                  {s.label}
                </p>
              </div>
            ))}
          </motion.div>
        </div>

        {/* image column */}
        <motion.div
          style={{ y: imgY }}
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="relative lg:col-span-5"
        >
          <div
            className="relative overflow-hidden rounded-[2rem] border border-white/10"
            data-testid="hero-visual"
          >
            <img
              src={IMAGES.hero}
              alt="Home theater com a Flash Play TV em 4K"
              className="h-[380px] w-full object-cover sm:h-[460px] lg:h-[560px]"
              loading="eager"
            />
            <div
              className="pointer-events-none absolute inset-0"
              style={{
                background:
                  "radial-gradient(circle at 28% 18%, rgba(0,240,255,0.28), transparent 55%), linear-gradient(to top, rgba(7,8,13,0.85), transparent 45%)",
              }}
              aria-hidden="true"
            />
            <div className="absolute left-5 top-5 inline-flex items-center gap-2 rounded-full border border-white/15 bg-ink/60 px-4 py-2 font-mono text-[10px] font-medium uppercase tracking-[0.2em] text-white backdrop-blur-md">
              <span className="animate-blink h-1.5 w-1.5 rounded-full bg-wa" />
              Ao vivo em 4K
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1, ease: [0.16, 1, 0.3, 1] }}
            className="animate-floaty absolute -bottom-7 right-4 rounded-3xl border border-wa/30 bg-ink/90 p-5 shadow-[0_24px_60px_-20px_rgba(37,211,102,0.5)] backdrop-blur-xl sm:right-8"
            data-testid="hero-annual-chip"
          >
            <p className="font-mono text-[10px] font-medium uppercase tracking-[0.25em] text-wa">
              Plano Anual • 12 meses
            </p>
            <p className="mt-1.5 font-display text-3xl font-extrabold text-white">
              R$ 20<span className="text-lg text-mist">/mês</span>
            </p>
            <p className="mt-0.5 font-mono text-[10px] uppercase tracking-wider text-mist">
              Por R$ 240,00 à vista
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
